// src/components/Home.js
import React, { useState, useEffect } from 'react';
import CuzprLogo from '../assets/CuzprLogo.png';
import { 
  FaRocket, 
  FaUsers, 
  FaChartLine, 
  FaArrowRight 
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// ModuleTitlesRotator cycles through module names every 3 seconds.
const ModuleTitlesRotator = () => {
  const sampleModules = [
    'Shopify Setup',
    'Klaviyo Optimization',
    'Google Ads Setup',
    'WordPress Optimization',
    'Meta Ads Setup',
    'BigCommerce Optimization',
  ];
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % sampleModules.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [sampleModules.length]);
  return <span className="text-blue-400">{sampleModules[currentIndex]}</span>;
};

// New LinearTimeline component – a linear roadmap with detailed descriptions.
const LinearTimeline = () => {
  return (
    <div className="space-y-10">
      {/* Timeline Item 1 */}
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <div className="w-12 h-12 bg-blue-600 rounded-full flex-shrink-0 flex items-center justify-center text-white mb-4 md:mb-0 md:mr-6">
          <FaChartLine className="text-3xl" />
        </div>
        <div>
          <h3 className="text-2xl font-semibold mb-2">Assessment</h3>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="text-sm text-gray-300">Access +100 brand modules</span>
            <FaArrowRight className="text-blue-400 transform transition-transform duration-300 hover:scale-125" />
            <span className="text-sm text-gray-300">Connect & Integrate all your channels</span>
            <FaArrowRight className="text-blue-400 transform transition-transform duration-300 hover:scale-125" />
            <span className="text-sm text-gray-300">Let CUZPR monitor & develop insights</span>
          </div>
          <hr className="border-t border-gray-600 my-2" />
          <p className="text-gray-300">
            Our AI-driven system meticulously evaluates your marketing funnel, digital presence, and current priorities to deliver a comprehensive baseline of your business health.
          </p>
        </div>
      </div>
      {/* Timeline Item 2 */}
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <div className="w-12 h-12 bg-green-600 rounded-full flex-shrink-0 flex items-center justify-center text-white mb-4 md:mb-0 md:mr-6">
          <FaUsers className="text-3xl" />
        </div>
        <div>
          <h3 className="text-2xl font-semibold mb-2">Guidance & Strategy</h3>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="text-sm text-gray-300">Explore & create project files</span>
            <FaArrowRight className="text-blue-400 transform transition-transform duration-300 hover:scale-125" />
            <span className="text-sm text-gray-300">Schedule & monitor calendar</span>
            <FaArrowRight className="text-blue-400 transform transition-transform duration-300 hover:scale-125" />
            <span className="text-sm text-gray-300">Master 50+ platforms like Shopify & Klaviyo</span>
          </div>
          <hr className="border-t border-gray-600 my-2" />
          <p className="text-gray-300">
            Based on your assessment, engage in a dynamic journey with interactive modules and expert-curated strategies—from setting up Shopify to mastering Klaviyo—tailored just for you.
          </p>
        </div>
      </div>
      {/* Timeline Item 3 */}
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <div className="w-12 h-12 bg-red-600 rounded-full flex-shrink-0 flex items-center justify-center text-white mb-4 md:mb-0 md:mr-6">
          <FaRocket className="text-3xl" />
        </div>
        <div>
          <h3 className="text-2xl font-semibold mb-2">Optimization & Growth</h3>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="text-sm text-gray-300">Access brands custom AI assistant</span>
            <FaArrowRight className="text-blue-400 transform transition-transform duration-300 hover:scale-125" />
            <span className="text-sm text-gray-300">Automatically manage & improve marketing</span>
            <FaArrowRight className="text-blue-400 transform transition-transform duration-300 hover:scale-125" />
            <span className="text-sm text-gray-300">Develop robust online business</span>
          </div>
          <hr className="border-t border-gray-600 my-2" />
          <p className="text-gray-300">
            Leverage real-time analytics, progress tracking, and AI-powered insights to continuously refine your strategy—ensuring you achieve sustainable growth while saving time and resources.
          </p>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Hero Section */}
      <header className="py-16 px-4 md:px-8">
        <div className="max-w-6xl mx-auto text-center space-y-6">
        <div className="flex justify-center">
          <img
            src={CuzprLogo}
            alt="Cuzpr Logo"
            className="h-auto w-auto animate-bounce"
          />
        </div>
          <h1 className="text-4xl md:text-6xl font-extrabold">
            Access powerful solutions for <br></br><ModuleTitlesRotator />
          </h1>
          <p className="text-lg md:text-xl">
            CUZPR is the intelligent platform that assesses your marketing readiness, funnel health, and priorities. Through personalized conversations and interactive modules, our AI assistant gathers actionable insights to help you optimize and scale your business.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-full font-semibold transition duration-300"
              onClick={() => navigate('/login')}
            >
              Login
            </button>
            <button className="bg-green-600 hover:bg-green-700 text-white py-3 px-8 rounded-full font-semibold transition duration-300">
              Join the Waitlist
            </button>
          </div>
        </div>
      </header>

      {/* Cards and Timeline Section */}
      <section className="px-4 md:px-8">
        <div className="max-w-6xl mx-auto space-y-12">
          <LinearTimeline />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-800">
        <div className="max-w-6xl mx-auto text-center text-sm">
          <p>© {new Date().getFullYear()} CUZPR. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
